import { useState, useEffect, useMemo } from "react";
import {
  Container,
  Paper,
  Title,
  Button,
  Text,
  Stack,
  Progress,
  Group,
  Center,
  Loader,
  Badge,
} from "@mantine/core";
import { useGetResponsesForSurvey, useGetSurveyById } from "../api/survey";
import { useParams, useSearchParams } from "react-router-dom";
import { usePageTitle } from "../services/HeaderTitleContext";
import { IconPrinter } from "@tabler/icons-react";
import { useSetActionButtons } from "../services/ActionButtonsContext";
import { printSurveyResponses } from "../utils/printSurveyResponses";

function SurveyResponses() {
  const { id: surveyId } = useParams();
  const { data: responses, isLoading } = useGetResponsesForSurvey(surveyId);
  const { data: survey } = useGetSurveyById(surveyId);
  const [searchParams, setSearchParams] = useSearchParams();

  const orderParam = Number(searchParams.get("questionId")) || 1;
  const [currentIndex, setCurrentIndex] = useState(0);

  usePageTitle(survey?.title ? `Responses - ${survey.title}` : null);

  useEffect(() => {
    if (responses) {
      const idx = responses.findIndex((q) => q.questionOrder === orderParam);
      setCurrentIndex(idx >= 0 ? idx : 0);
    }
  }, [orderParam, responses]);

  const actions = useMemo(
    () => [
      {
        label: "Print responses",
        onClick: () => printSurveyResponses(responses, survey?.title),
        icon: <IconPrinter />,
        disabled: !responses?.length,
      },
    ],
    [responses, survey?.title]
  );

  useSetActionButtons(actions);

  if (isLoading)
    return (
      <Center>
        <Loader />
      </Center>
    );
  if (!responses || responses.length === 0)
    return <Text>No responses for this survey.</Text>;

  const totalQuestions = responses.length;
  const current = responses[currentIndex];

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % totalQuestions;
    setCurrentIndex(nextIndex);
    setSearchParams({ questionId: responses[nextIndex].questionOrder });
  };

  const handlePrev = () => {
    const prevIndex = (currentIndex - 1 + totalQuestions) % totalQuestions;
    setCurrentIndex(prevIndex);
    setSearchParams({ questionId: responses[prevIndex].questionOrder });
  };

  const renderQuestion = (q) => {
    if (q.questionType === "text") {
      if (!q.answers || q.answers.length === 0) {
        return <Text>No answers yet (0 responses)</Text>;
      }

      return (
        <Stack gap="xs">
          {q.answers.map((ansObj, idx) => (
            <Paper key={idx} withBorder p="xs">
              <Text>
                {ansObj.answer} ({ansObj.count})
              </Text>
            </Paper>
          ))}
        </Stack>
      );
    }

    if (
      q.questionType === "single_choice" ||
      q.questionType === "multiple_choice"
    ) {
      const total = q.totalResponses || 0;

      return (
        <Stack gap="xs">
          {q.answerOptions?.map((opt, idx) => {
            const count = opt.count || 0;
            const percent = total > 0 ? (count / total) * 100 : 0;

            return (
              <Paper key={idx} withBorder p="xs">
                <Text>
                  {opt.labelI18n?.en} ({count} / {total} ~ {Math.round(percent)}
                  %)
                </Text>
                <Progress value={percent} size="sm" mt="xs" />
              </Paper>
            );
          })}
        </Stack>
      );
    }
  };

  return (
    <Container size="sm" my="md">
      <Paper withBorder p="md">
        <Group justify="space-between" align="center" mb="md">
          <Title order={3}>
            Question {currentIndex + 1} / {totalQuestions}
          </Title>
          <Badge size="lg" variant="light" color="blue">
            Total responses: {responses?.[currentIndex].totalResponses}
          </Badge>
        </Group>

        <Text fw={500} mb="sm">
          {current.questionText} (
          {current.questionType.replace("_", " ").toUpperCase()})
        </Text>

        {renderQuestion(current)}

        <Group mt="md" justify="space-between">
          <Button onClick={handlePrev}>Previous</Button>
          <Button onClick={handleNext}>Next</Button>
        </Group>
      </Paper>
    </Container>
  );
}

export default SurveyResponses;
